import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import OrionTopBar from './OrionTopBar';
import OrionLeftPanel from './OrionLeftPanel';
import OrionCenterPanel from './OrionCenterPanel';
import OrionRightPanel from './OrionRightPanel';
import LiveTalkButton from './voice/LiveTalkButton';

export default function OrionDashboard({ onSwitchAgent, onSwitchToShow, onSwitchToVega, onSwitchToAtlas, onSwitchToCrew }) {
  const [worldState, setWorldState] = useState(null);
  const [selectedInsightId, setSelectedInsightId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get('/api/orion/state');
      setWorldState(res.data);
      if (!selectedInsightId && res.data.insights?.length > 0) {
        const top = res.data.insights.reduce((a, b) => a.severity > b.severity ? a : b);
        setSelectedInsightId(top.id);
      }
    } catch (err) {
      console.error('Failed to fetch Orion state:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedInsightId]);

  useEffect(() => {
    fetchState();
    const interval = setInterval(fetchState, 30000);
    return () => clearInterval(interval);
  }, [fetchState]);

  const handleRefresh = useCallback(async () => {
    try {
      await api.post('/api/orion/refresh');
      await fetchState();
    } catch {}
  }, [fetchState]);

  const selectedInsight = worldState?.insights?.find(i => i.id === selectedInsightId) || null;
  const scoring = worldState?.scoring || {};

  if (loading) {
    return (
      <div className="orion-loading" data-testid="orion-loading-screen">
        <div className="orion-loading-ring"></div>
        <div className="orion-loading-text">ORION</div>
        <div className="orion-loading-sub">Linking to space weather sources...</div>
      </div>
    );
  }

  if (!worldState) {
    return (
      <div className="orion-loading" data-testid="orion-error-screen">
        <div className="orion-loading-text" style={{ color: '#FF3B30' }}>LINK LOST</div>
        <div className="orion-loading-sub">Unable to reach Orion Brain</div>
      </div>
    );
  }

  return (
    <div className="orion-app" data-testid="orion-app">
      <OrionTopBar
        timestamp={worldState.timestamp}
        signalCount={worldState.signal_count}
        totalInsights={worldState.total_insight_count}
        state={worldState.state}
        scoring={scoring}
        sensorReadiness={worldState.sensor_readiness}
        onRefresh={handleRefresh}
        onSwitchAgent={onSwitchAgent}
        onSwitchToShow={onSwitchToShow}
        onSwitchToVega={onSwitchToVega}
        onSwitchToAtlas={onSwitchToAtlas}
        onSwitchToCrew={onSwitchToCrew}
      />
      <div className="orion-body">
        <OrionLeftPanel
          state={worldState.state}
          scoring={scoring}
          insights={worldState.insights}
        />
        <OrionCenterPanel
          insights={worldState.insights}
          offlineNotices={worldState.offlineNotices}
          selectedInsightId={selectedInsightId}
          onSelectInsight={setSelectedInsightId}
          scoring={scoring}
        />
        <OrionRightPanel
          insight={selectedInsight}
          insights={worldState.insights}
          selectedInsightId={selectedInsightId}
          onSelectInsight={setSelectedInsightId}
          scoring={scoring}
        />
      </div>
      <LiveTalkButton agent="orion" label="Talk to Orion" />
    </div>
  );
}
